import Ajv, { ErrorObject, ValidateFunction } from 'ajv';
import addFormats from 'ajv-formats';
import weaponSchema from '../data/schemas/weapon.schema.json';
import passiveSchema from '../data/schemas/passive.schema.json';
import evolutionSchema from '../data/schemas/evolution.schema.json';
import consumableSchema from '../data/schemas/consumable.schema.json';
import upgradeSchema from '../data/schemas/upgrade.schema.json';
import enemySchema from '../data/schemas/enemy.schema.json';
import waveSchema from '../data/schemas/wave.schema.json';
import gameSchema from '../data/schemas/game.schema.json';
import biomeSchema from '../data/schemas/biome.schema.json';
import { createLogger, Logger } from './Logger';

type ValidationResult = { isValid: boolean; errors?: string[] };

export class DataValidator {
  private ajv: Ajv;
  private validators: Map<string, ValidateFunction>;
  private logger: Logger;

  constructor() {
    this.logger = createLogger('DataValidator');
    this.logger.debug('constructor:start');

    this.ajv = new Ajv({ allErrors: true });
    addFormats(this.ajv);

    this.validators = new Map();
    this.compileSchema('weapon', weaponSchema);
    this.compileSchema('passive', passiveSchema);
    this.compileSchema('evolution', evolutionSchema);
    this.compileSchema('consumable', consumableSchema);
    this.compileSchema('upgrade', upgradeSchema);
    this.compileSchema('enemy', enemySchema);
    this.compileSchema('wave', waveSchema);
    this.compileSchema('game', gameSchema);
    this.compileSchema('biome', biomeSchema);

    this.logger.debug('constructor:complete', {
      schemaIds: Array.from(this.validators.keys())
    });
  }

  /**
   * Validates data against a specific schema
   * @param schemaId The ID of the schema to validate against
   * @param data The data to validate
   * @returns An object containing validity status and error messages if invalid
   */
  validate(schemaId: string, data: unknown): ValidationResult {
    this.logger.debug('validate:start', { schemaId });

    const validator = this.validators.get(schemaId);
    
    if (!validator) {
      this.logger.warn('validate:unknown-schema', {
        schemaId,
        availableSchemaIds: Array.from(this.validators.keys())
      });

      return {
        isValid: false,
        errors: [`Unknown schema ID: ${schemaId}`]
      };
    }

    const valid = validator(data);
    
    if (!valid && validator.errors) {
      const errors = this.formatErrors(validator.errors);
      this.logger.error('validate:invalid-data', {
        schemaId,
        errors,
        ajvErrors: validator.errors
      });

      return {
        isValid: false,
        errors
      };
    }

    this.logger.debug('validate:complete', { schemaId, isValid: true });

    return { isValid: true };
 }

  private compileSchema(schemaId: string, schema: object): void {
    this.logger.debug('compileSchema:start', { schemaId });
    const validator = this.ajv.compile(schema);
    this.validators.set(schemaId, validator);
    this.logger.debug('compileSchema:complete', { schemaId });
  }

  /**
   * Formats validation errors into human-readable messages
   * @param errors The error objects from AJV
   * @returns An array of formatted error messages
   */
  private formatErrors(errors: ErrorObject[]): string[] {
    return errors.map(error => {
      // Extract property name from the error path
      const property = error.instancePath
        ? error.instancePath.replaceAll('/', '.').replace(/^\./, '')
        : error.params?.['additionalProperty'];
      
      switch (error.keyword) {
        case 'required':
          return `Missing required property: ${error.params?.['missingProperty']}`;
        case 'type':
          return `Invalid type for property "${property}". Expected: ${error.params?.['type']}`;
        case 'minimum':
          return `Value for property "${property}" is below minimum. Minimum: ${error.params?.['limit']}`;
        case 'maximum':
          return `Value for property "${property}" exceeds maximum. Maximum: ${error.params?.['limit']}`;
        case 'enum':
          return `Invalid value for property "${property}". Allowed values: ${error.params?.['allowedValues']?.join(', ')}`;
        case 'additionalProperties':
          return `Unknown property "${property}" is not allowed in this schema`;
        default:
          return `${error.instancePath || 'Data'} ${error.message || 'has validation errors'}`;
      }
    });
  }
}
